import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Users, Phone as PhoneIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VideoPlayer } from '../components/VideoPlayer';
export function RecruitingPage() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-blue-600/20" data-id="element-110">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm" data-id="element-111">
        <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between" data-id="element-112">
          <Link to="/" className="flex items-center" aria-label="Home" data-id="element-113">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-xl flex items-center justify-center p-2 shadow-sm border border-slate-100" data-id="element-114">
              <img src="https://cdn.magicpatterns.com/uploads/kRybL6nc1toe2tMoAuDrgk/IMG_1492.jpg" alt="Team logo" className="w-full h-full object-contain" data-id="element-115" />
            </div>
          </Link>
          <Link to="/agents" className="text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors flex items-center gap-1" data-id="element-116">
            Agent Login <ChevronRight className="w-4 h-4" data-id="element-117" />
          </Link>
        </div>
      </nav>

      {/* Hero Section (Main VSL) */}
      <section className="pt-40 pb-20 px-6 relative overflow-hidden" data-id="element-118">
        {/* Background Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none" data-id="element-119"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-120">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-121">
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-6 leading-[1.1] text-slate-900" data-id="element-122">
              WHERE AMBITION MEETS{' '}
              <span className="text-blue-600" data-id="element-123">OPPORTUNITY.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-12 max-w-3xl mx-auto font-medium" data-id="element-124">
              Stop building someone else's dream. Watch the video below to
              discover how our proven system creates top-producing agents and
              agency owners.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="w-full max-w-4xl mx-auto" data-id="element-125">
            <VideoPlayer loomId="d2c24158900349f481ea3e2c0fff214f" autoPlay title="The Blueprint" subtitle="Compensation, Training & Growth" className="ring-4 ring-white shadow-[0_20px_50px_rgba(37,99,235,0.15)]" data-id="element-126" />
          </motion.div>
        </div>
      </section>

      {/* Deep Dive Section */}
      <section className="py-24 px-6 bg-white border-y border-slate-200" data-id="element-127">
        <div className="max-w-5xl mx-auto" data-id="element-128">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-center mb-16" data-id="element-129">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-slate-900" data-id="element-130">
              THE BLUEPRINT TO SUCCESS
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-131">
            <VideoPlayer youtubeId="DKquR1r4oKs" startSeconds={2} autoPlay title="The Opportunity" subtitle="Why now is the time to join us" data-id="element-132" />
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6 relative bg-slate-50" data-id="element-133">
        <div className="max-w-7xl mx-auto" data-id="element-134">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-135">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 border border-blue-100 mb-6 shadow-sm" data-id="element-136">
              <Users className="w-8 h-8 text-blue-600" data-id="element-137" />
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 uppercase text-slate-900" data-id="element-138">
              Hear From The Frontline
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto font-medium" data-id="element-139">
              Real agents. Real numbers. Real life-changing results. Don't just
              take our word for it.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-140">
            {([{
            name: 'Ryan D.',
            role: 'Agent',
            delay: 0.1,
            youtubeId: 'Mftop72MdEk'
          }, {
            name: 'Kristi D.',
            role: 'Agent',
            delay: 0.15,
            youtubeId: 'XZCCxAnrYW0'
          }, {
            name: 'Cody C.',
            role: 'Agent',
            delay: 0.2,
            youtubeId: 'M6vGkSOGHM0'
          }, {
            name: 'Mason H.',
            role: 'Top Producer',
            delay: 0.3,
            youtubeId: 'qLam8Uc09eo'
          }, {
            name: 'Jackson R.',
            role: 'Agent',
            delay: 0.4,
            youtubeId: 'xEGLnQ8w2Rk'
          }, {
            name: 'Nathen G.',
            role: 'Agent',
            delay: 0.5,
            youtubeId: 'ou5y7sRmryk'
          }, {
            name: 'Connor T.',
            role: 'Regional Sales Manager',
            delay: 0.6,
            youtubeId: 'R4Rwh2DUve4'
          }, {
            name: 'Hunter H.',
            role: 'Agent',
            delay: 0.7,
            youtubeId: 'zHj_wbp28cM'
          }] as {
            name: string;
            role: string;
            delay: number;
            youtubeId?: string;
          }[]).map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: testimonial.delay
          }} className="" data-id="element-141">
                <VideoPlayer aspectRatio="portrait" youtubeId={testimonial.youtubeId} title={testimonial.name} subtitle={testimonial.role} className="h-full" data-id="element-142" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Fixed CTA / Bottom CTA */}
      <section className="py-20 px-6 bg-blue-600 text-white text-center relative overflow-hidden" data-id="element-143">
        <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
      }} data-id="element-144"></div>
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-145">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 uppercase" data-id="element-146">
            Ready to change your life?
          </h2>
          <p className="text-xl font-medium mb-10 opacity-90" data-id="element-147">
            Call now to schedule your interview and take the first step toward
            building your own agency.
          </p>
          <a href="tel:+16306393533" className="bg-white text-blue-900 text-2xl md:text-3xl font-extrabold py-5 px-12 rounded-full hover:bg-slate-50 hover:scale-105 transition-all shadow-2xl inline-flex items-center gap-3 mx-auto tracking-tight" data-id="element-148">
            <PhoneIcon className="w-7 h-7 text-blue-600" data-id="element-149" />
            630-639-3533
          </a>
          <p className="mt-6 text-sm font-semibold uppercase tracking-wider opacity-80" data-id="element-150">
            Call to schedule your interview
          </p>
        </div>
      </section>
    </div>;
}